import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Download, Printer, TrendingUp, TrendingDown, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function StudentLedger() {
  const { rid } = useParams();
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const [data, setData] = useState(null);
  const [delFee, setDelFee] = useState(null);        // fee entry pending confirm
  const [voidPay, setVoidPay] = useState(null);      // payment entry pending confirm
  const [voidReason, setVoidReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/residents/${rid}/ledger`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [rid]);

  const receipts = useMemo(() => {
    const map = {};
    (data?.payments || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [data]);

  // v42 — Admin deletes an incorrect/duplicate fee entry (soft-archive backend).
  const confirmDeleteFee = async () => {
    if (!delFee) return;
    setBusy(true);
    try {
      await api.delete(`/fees/${delFee.reference}`);
      toast.success("Fee deleted. Balance recalculated.");
      setDelFee(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // v42 — Admin voids an incorrect/duplicate payment (soft-reverse backend).
  const confirmVoidPayment = async () => {
    if (!voidPay || !voidReason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      await api.delete(`/payments/${voidPay.reference}?reason=${encodeURIComponent(voidReason.trim())}`);
      toast.success("Payment voided. Balance recalculated.");
      setVoidPay(null); setVoidReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const { resident, entries = [], total_billed, total_paid, balance } = data;

  const downloadReceipt = async (paymentId) => {
    try {
      const token = localStorage.getItem("hostel_token");
      const res = await fetch(`${API}/payments/${paymentId}/receipt`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `receipt-${paymentId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download receipt"); }
  };

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <Link to="/residents" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to residents
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Student Ledger</div>
          <h1 className="font-display text-4xl font-bold mt-1">{resident.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {[resident.room_no && `Room ${resident.room_no}`, resident.phone]
              .filter(Boolean).join(" • ") || "—"}
          </div>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="print-ledger">
          <Printer className="w-4 h-4 mr-2" /> Print
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-5 border-border bg-white" data-testid="ledger-billed">
          <div className="eyebrow text-muted-foreground">Total Billed</div>
          <div className="font-display text-3xl font-bold mt-2">{fmt(total_billed)}</div>
        </Card>
        <Card className="p-5 border-border bg-white" data-testid="ledger-paid">
          <div className="eyebrow text-primary">Total Paid</div>
          <div className="font-display text-3xl font-bold mt-2">{fmt(total_paid)}</div>
        </Card>
        <Card className="p-5 border-border bg-white" data-testid="ledger-balance">
          <div className={`eyebrow ${balance > 0 ? "text-destructive" : "text-primary"}`}>Balance</div>
          <div className={`font-display text-3xl font-bold mt-2 ${balance > 0 ? "text-destructive" : ""}`}>
            {fmt(balance)}
          </div>
        </Card>
      </div>

      {/* v23.1 — Chronological ledger with running balance */}
      <Card className="p-4 border-border bg-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="eyebrow text-muted-foreground">Ledger</div>
            <div className="font-display text-lg font-bold">All Transactions</div>
          </div>
          <div className="text-xs text-muted-foreground">
            Oldest first · debits (fees) increase balance, credits (payments) reduce it
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-entries-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-4">Date</th>
                <th className="py-3 pr-4">Kind</th>
                <th className="py-3 pr-4">Module</th>
                <th className="py-3 pr-4">Description</th>
                <th className="py-3 pr-4 text-right">Debit</th>
                <th className="py-3 pr-4 text-right">Credit</th>
                <th className="py-3 pr-4 text-right">Balance</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const pay = e.kind === "payment" ? receipts[e.reference] : null;
                return (
                  <tr key={`${e.reference}-${i}`} className="border-b border-border/50" data-testid={`ledger-entry-${e.reference}`}>
                    <td className="py-3 pr-4 font-mono text-xs">{e.date || "—"}</td>
                    <td className="py-3 pr-4">
                      {e.kind === "fee" ? (
                        <Badge variant="destructive" className="capitalize"><TrendingUp className="w-3 h-3 mr-1" />Fee</Badge>
                      ) : (
                        <Badge className="bg-primary text-primary-foreground capitalize"><TrendingDown className="w-3 h-3 mr-1" />Payment</Badge>
                      )}
                    </td>
                    <td className="py-3 pr-4 capitalize text-xs">{e.module || "—"}</td>
                    <td className="py-3 pr-4">
                      <div>{e.description}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {e.invoice_no || e.receipt_no || ""}
                        {e.entry_date && e.entry_date !== e.date && ` · entered ${e.entry_date}`}
                        {e.status && ` · ${e.status}`}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-right font-mono">{e.debit > 0 ? fmt(e.debit) : "—"}</td>
                    <td className="py-3 pr-4 text-right font-mono text-primary">{e.credit > 0 ? fmt(e.credit) : "—"}</td>
                    <td className={`py-3 pr-4 text-right font-mono font-semibold ${e.balance > 0 ? "text-destructive" : ""}`}>
                      {fmt(e.balance)}
                    </td>
                    <td className="py-3 pr-4 text-right whitespace-nowrap">
                      {pay && (
                        <Button size="sm" variant="outline" onClick={() => downloadReceipt(pay.id)}
                                data-testid={`ledger-receipt-${pay.id}`}>
                          <Download className="w-3 h-3 mr-1" /> Receipt
                        </Button>
                      )}
                      {canDelete && e.kind === "fee" && (
                        <Button size="sm" variant="ghost" className="ml-1"
                                onClick={() => setDelFee(e)}
                                data-testid={`ledger-delete-fee-${e.reference}`}
                                title="Delete this fee entry">
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      )}
                      {canDelete && e.kind === "payment" && (
                        <Button size="sm" variant="ghost" className="ml-1"
                                onClick={() => setVoidPay(e)}
                                data-testid={`ledger-void-payment-${e.reference}`}
                                title="Void this payment">
                          <XCircle className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {entries.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No ledger activity yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* v42 — Delete fee confirm */}
      <Dialog open={!!delFee} onOpenChange={(v) => !v && setDelFee(null)}>
        <DialogContent data-testid="ledger-delete-fee-dialog">
          <DialogHeader>
            <DialogTitle>Delete this fee entry?</DialogTitle>
            <DialogDescription>
              {delFee && (
                <>
                  {delFee.description} · {fmt(delFee.debit)} · {delFee.date}.
                  <br /><br />
                  The fee is soft-deleted: it disappears from this ledger, Outstanding Dues, Dashboard dues
                  and every fee module. Linked payments stay preserved for audit. Balance recalculates
                  automatically.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelFee(null)}>Cancel</Button>
            <Button variant="destructive" disabled={busy} onClick={confirmDeleteFee}
                    data-testid="ledger-confirm-delete-fee">
              {busy ? "Deleting…" : "Delete Fee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v42 — Void payment confirm */}
      <Dialog open={!!voidPay} onOpenChange={(v) => !v && setVoidPay(null)}>
        <DialogContent data-testid="ledger-void-payment-dialog">
          <DialogHeader>
            <DialogTitle>Void this payment?</DialogTitle>
            <DialogDescription>
              {voidPay && (
                <>
                  {voidPay.description} · {fmt(voidPay.credit)} · {voidPay.date}.
                  <br /><br />
                  Voided payments stop counting in Dashboard collections, P&L and all reports immediately.
                  The original record stays in the audit log with your reason.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label>Reason for void</Label>
            <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                   placeholder="e.g. Duplicate entry / wrong amount"
                   data-testid="ledger-void-reason" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidPay(null)}>Cancel</Button>
            <Button variant="destructive" disabled={busy || !voidReason.trim()}
                    onClick={confirmVoidPayment}
                    data-testid="ledger-confirm-void-payment">
              {busy ? "Voiding…" : "Confirm Void"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
